// Panels inside an expanded run on Claims → Runs.
//
// Declared inside the page component until 2026-10-20, which made React treat
// each one as a new component on every render and remount it.

import { format } from 'date-fns';
import { CATEGORY_MEANING } from '../../../lib/edi/x276';
import { fmt$2, fmtDate } from './format';

// What GAMMIS said when last asked, where the answer is one somebody has to
// act on. A claim still being adjudicated and a claim GAMMIS has never heard
// of both sit at `submitted` and look identical in the table below; only one
// of them means a week of care was never actually filed.
const ANOMALY = ['not_found', 'unprocessable', 'request_error'];

export function StatusAnomalies({ lines }) {
  const flagged = lines.filter(l => ANOMALY.includes(l.last_status_category));
  if (!flagged.length) return null;
  return (
    <div style={{ margin: '12px 0', border: '0.5px solid var(--danger)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', background: 'var(--danger-bg)', fontSize: 11, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--danger)' }}>
        {flagged.length} claim{flagged.length === 1 ? '' : 's'} GAMMIS could not answer for
      </div>
      {flagged.slice(0, 10).map((l, i) => (
        <div key={l.id} style={{ padding: '8px 12px', borderTop: i ? '0.5px solid var(--border)' : 'none', fontSize: 11.5 }}>
          <span style={{ fontWeight: 600 }}>{l.client_name || '—'}</span>
          <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{fmtDate(l.service_date)}</span>
          <div style={{ color: 'var(--text2)', marginTop: 2, lineHeight: 1.5 }}>
            {CATEGORY_MEANING[l.last_status_category]}
            {l.last_status_code ? ` (${l.last_status_code})` : ''}
          </div>
        </div>
      ))}
      {flagged.length > 10 && (
        <div style={{ padding: '6px 12px', fontSize: 11, color: 'var(--text3)' }}>
          + {flagged.length - 10} more
        </div>
      )}
    </div>
  );
}

// Remittances that have arrived and not yet been posted.
//
// This exists because the transport worker broke an assumption that held
// everywhere else: that a remittance is read and posted in the same breath,
// by the person who chose the file. The worker fetches one on its own, and
// without somewhere for it to appear the money would sit in a table nobody
// opens — fetched, parsed, and never applied to a single line.
export function PendingRemittances({ run, pendingRemits, onOpen }) {
  const mine    = pendingRemits.filter(r => r.billing_run_id === run.id);
  const orphans = pendingRemits.filter(r => !r.billing_run_id);
  if (!mine.length && !orphans.length) return null;

  const row = (r, tied) => (
    <div key={r.id} style={{ padding: '10px 12px', borderTop: '0.5px solid var(--border)',
                             display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
          {fmt$2(r.payment_amount)}
          <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 8 }}>
            {r.payer_name || 'payer not named'}
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, lineHeight: 1.5 }}>
          {[
            r.trace_number ? `trace ${r.trace_number}` : null,
            r.payment_date ? `paid ${fmtDate(r.payment_date)}` : null,
            `received ${format(new Date(r.imported_at), 'MMM d, HH:mm')}`,
          ].filter(Boolean).join(' · ')}
        </div>
        {!tied && (
          <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 2 }}>
            Not tied to a run — none of its claims match a line we filed. Check this is the right run before posting.
          </div>
        )}
      </div>
      <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }}
        onClick={() => onOpen(r, run)}>Review and post</button>
    </div>
  );

  return (
    <div style={{ margin: '12px 0', border: '0.5px solid var(--success)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', background: 'var(--success-bg)', fontSize: 11, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--success)' }}>
        Remittance received — not yet posted
      </div>
      {mine.map(r => row(r, true))}
      {orphans.map(r => row(r, false))}
    </div>
  );
}

export function SortArrow({ field, sortKey, sortAsc }) {
  return (
    <span style={{ marginLeft: 3, fontSize: 9, opacity: sortKey === field ? 1 : 0.3 }}>
      {sortKey === field ? (sortAsc ? '↑' : '↓') : '⇅'}
    </span>
  );
}

// Claims a remittance could not be placed, and matches made by guesswork.
// One component for both paths now that both produce the same shape.
export function RemittanceOutcome({ results }) {
  if (!results) return null;
  const { unmatched = [], uncertain = [] } = results;
  const line = (c) => [
    c.patientName || c.medicaidId || '?',
    c.serviceLines?.[0]?.fromDate || '?',
    c.serviceLines?.[0]?.serviceCode || '',
    fmt$2(c.paid),
  ].filter(Boolean).join(' · ');

  return (
    <>
      {uncertain.length > 0 && (
        <div style={{ textAlign: 'left', background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--warn)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            {uncertain.length} matched by member, date and code
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>
            These claims carried no control number or ICN, so the line was inferred. Worth a look where a client has more than one line of the same code on a day.
          </div>
        </div>
      )}
      {unmatched.length > 0 && (
        <div style={{ textAlign: 'left', background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--warn)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            ⚠ Unmatched — no claim line found; edit the line manually to record it
          </div>
          {unmatched.map((u, i) => (
            <div key={i} style={{ fontSize: 11, color: 'var(--text2)', padding: '3px 0', borderBottom: '0.5px solid var(--border)', fontFamily: 'monospace' }}>
              {line(u)}
              {u._error && <span style={{ color: 'var(--danger)', marginLeft: 8 }}>Error: {u._error}</span>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
